import React, { Fragment } from 'react'
import PropTypes from 'prop-types';
import { Menu, Transition } from '@headlessui/react'
import { useUser } from '@lore/auth';
import avatar from '../../assets/images/avatar.svg';
import { Link } from 'react-router-dom';
import { useConnect, useQueryMutation } from '@lore/query-connect';
import { useConfig } from '@lore/config';
import classNames from 'classnames';
import useIntents from '../hooks/useIntents';

function MenuItem(props) {
  const { to, link = false, children } = props;

  return (
    <Menu.Item>
      {({ active }) => link ? (
        <a
          href={to}
          className={classNames(
            active ? 'bg-cs-gray-100 text-cs-gray-900' : 'text-cs-gray-900',
            'block px-4 py-2 text-base'
          )}
        >
          {children}
        </a>
      ) : (
        <Link
          to={to}
          className={classNames(
            active ? 'bg-cs-gray-100 text-cs-gray-900' : 'text-cs-gray-900',
            'block px-4 py-2 text-base'
          )}
        >
          {children}
        </Link>
      )}
    </Menu.Item>
  );
}

MenuItem.propTypes = {
  to: PropTypes.string.isRequired,
  link: PropTypes.bool,
  children: PropTypes.node
};


export default function Account() {
  const currentUser = useUser();

  const { isEducator, isPartner } = useIntents();

  const config = useConfig();
  const stewardUrl = config.custom.stewardUrl();

  const mutation = useQueryMutation('currentUser.updateSingleton', currentUser);

  function getAdminUrl(network) {
    return `${stewardUrl}?networks=${network.id}`;
  }
  const networks = useConnect('network.find', {
    where: {
      eager: {
        $where: {
          isActive: true
        }
      }
    }
  });

  const memberNetworks = useConnect('network.find', {
    where: {
      eager: {
        $where: {
          networkStewards: {
            $where: {
              userId: currentUser.id
            }
          }
        }
      }
    }
  });

  function onChangeCommunity(network) {
    mutation.mutate({
      networkId: network.id
    });
  }

  return (
    <Menu as="div" className="relative inline-block text-left w-full">
      <div>
        <Menu.Button className={classNames(
          'group w-full bg-white rounded-cs-10 p-3',
          'shadow-cs-flat -m-px border border-cs-gray-200',
          'hover:bg-cs-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-cs-orange-500'
        )}>
          <div className="flex space-x-3 text-left">
            <img
              className="w-12 h-12 rounded-full"
              src={currentUser.data.avatarUrl || avatar}
              alt=""
            />
            <span className="flex-1 flex flex-col min-w-0">
              <span className="text-cs-gray-900 text-lg font-semibold">
                {`${currentUser.data.firstName} ${currentUser.data.lastName}`.trim() || 'Profile'}
              </span>
              <span className="text-cs-gray-500 text-sm truncate">
                {isEducator && isPartner ? 'Educator / Partner' : isEducator ? 'Educator' : 'Partner'}
              </span>
            </span>
          </div>
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="z-10 origin-top absolute right-0 left-0 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
          <div className="py-1">
            <MenuItem to={`/profile/view`}>
              View profile
            </MenuItem>
            <MenuItem to={`/profile/edit`}>
              Edit profile
            </MenuItem>
            <MenuItem to={`/profile/settings`}>
              Settings
            </MenuItem>
          </div>
          {memberNetworks.data.length > 0 && (
            <div className="py-1">
              {memberNetworks.data.map(function(network, index) {
                return (
                  <MenuItem key={index} to={getAdminUrl(network)} link={true}>
                    {memberNetworks.data.length > 1 ?
                      `Admin: ${network.data?.name}` :
                      `Admin`
                    }
                  </MenuItem>
                );
              })}
            </div>
          )}
          {currentUser.data.isStaff && (
            <div className="py-1">
              <MenuItem to={stewardUrl} link={true}>
                Admin
              </MenuItem>
            </div>
          )}
          {currentUser.data.isSuperuser && (
            <div className="py-1">
              <div
                className={classNames(
                  'text-cs-gray-900',
                  'block px-4 py-2 text-base cursor-pointer bg-cs-orange-50'
                )}
              >
                Switch community to
              </div>
              {networks.data.map(function(network) {
                return (
                  <Menu.Item key={network.id}>
                    {({ active }) => (
                      <div
                        onClick={() => onChangeCommunity(network)}
                        className={classNames(
                          active ? 'bg-cs-gray-100 text-cs-gray-900' : 'text-cs-gray-900',
                          'block px-4 py-2 text-base cursor-pointer bg-cs-orange-50'
                        )}
                      >
                        <span className="text-cs-orange-500 font-semibold">{network.data?.name}</span>
                      </div>
                    )}
                  </Menu.Item>
                );
              })}
              <Menu.Item>
                {({ active }) => (
                  <div
                    onClick={() => onChangeCommunity({ id: null })}
                    className={classNames(
                      active ? 'bg-cs-gray-100 text-cs-gray-900' : 'text-cs-gray-900',
                      'block px-4 py-2 text-base cursor-pointer bg-cs-orange-50'
                    )}
                  >
                    <span className="text-cs-orange-500 font-semibold">[None]</span>
                  </div>
                )}
              </Menu.Item>
            </div>
          )}
          <div className="py-1">
            <MenuItem to="/logout">
              Logout
            </MenuItem>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
